import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../services/api.service';
import { IndividualContestActivity } from '../../models/types';
import { cardSx, formatHandle } from '../ui/uiHelpers';
import { AthleteActivityPanel } from './AthleteActivityPanel';
import { AthleteActivityItem } from './athleteActivityStats';

interface Participant {
  userId: string;
  name: string;
  username: string;
  stravaProfile?: {
    firstname?: string;
    lastname?: string;
    profile?: string;
  };
}

interface ParticipantActivityDetailProps {
  contestId: string;
  participant: Participant;
  onBack: () => void;
}

export const ParticipantActivityDetail: React.FC<ParticipantActivityDetailProps> = ({
  contestId,
  participant,
  onBack
}) => {
  const { t } = useTranslation('contests');
  const [activities, setActivities] = useState<IndividualContestActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Silent reloads keep the list on screen while refreshing after an admin review.
  const fetchActivities = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(false);
      setActivities(await apiService.getIndividualContestActivities(contestId, participant.userId));
    } catch (err) {
      console.error('Error fetching participant activities:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [contestId, participant.userId]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Refetch rather than patch locally: a Strava edit rebuilds records under new ids.
  const handleReviewSuspicious = async (activityId: string, dismissed: boolean) => {
    await apiService.reviewSuspiciousActivity(contestId, activityId, dismissed);
    await fetchActivities(true);
  };

  const displayName =
    participant.stravaProfile?.firstname && participant.stravaProfile?.lastname
      ? `${participant.stravaProfile.firstname} ${participant.stravaProfile.lastname}`
      : participant.name || participant.username || t('userFallback', { id: participant.userId });

  const items: AthleteActivityItem[] = useMemo(
    () => activities.map(a => ({ ...a, id: a._id })),
    [activities]
  );

  return (
    <Box sx={{ ...cardSx, overflow: 'hidden' }}>
      <AthleteActivityPanel
        name={displayName}
        subtitle={formatHandle(participant.username)}
        activities={items}
        loading={loading}
        error={error ? t('athlete.loadError') : null}
        onBack={onBack}
        onReviewSuspicious={handleReviewSuspicious}
      />
    </Box>
  );
};
